import React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Eye, EyeOff } from "lucide-react";
import { useDashTheme } from "../hooks/useDashTheme";

const baseField = (t, invalid) => ({
  width: "100%", minHeight: 40, padding: "9px 12px", borderRadius: t.r.sm,
  border: `1px solid ${invalid ? t.status.negative : t.c.border}`, background: t.c.surface,
  color: t.c.text, fontSize: t.font.size.md, fontFamily: t.font.family, outline: "none",
  transition: "border-color 120ms, box-shadow 120ms",
});

export function Field({ label, required, hint, error, children }) {
  const t = useDashTheme();
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {label && (
        <span style={{ fontSize: t.font.size.sm, fontWeight: 500, color: t.c.textSecondary }}>
          {label}{required && <span style={{ color: t.status.negative }}> *</span>}
        </span>
      )}
      {children}
      {error ? <span style={{ fontSize: t.font.size.xs, color: t.status.negative }}>{error}</span>
        : hint ? <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>{hint}</span> : null}
    </label>
  );
}

export function Input({ invalid, style, ...rest }) {
  const t = useDashTheme();
  // Locked (disabled/readOnly) inputs: muted background + clearly-readable value
  // (not placeholder-faint). WebkitTextFillColor overrides the browser's default
  // graying of disabled inputs so the actual value stays legible.
  const locked = rest.disabled || rest.readOnly;
  const disabledStyle = locked
    ? { background: t.c.surfaceMuted, color: t.c.textSecondary, WebkitTextFillColor: t.c.textSecondary, opacity: 1, cursor: "not-allowed" }
    : null;
  return (
    <input
      {...rest}
      style={{ ...baseField(t, invalid), ...disabledStyle, ...style }}
      onFocus={(e) => { if (locked) return; e.target.style.borderColor = t.c.primary; e.target.style.boxShadow = `0 0 0 3px ${t.c.primaryRing}`; rest.onFocus?.(e); }}
      onBlur={(e) => { if (locked) return; e.target.style.borderColor = invalid ? t.status.negative : t.c.border; e.target.style.boxShadow = "none"; rest.onBlur?.(e); }}
    />
  );
}

// Password input with a show/hide eye toggle. Same look as Input.
export function PasswordInput({ invalid, style, ...rest }) {
  const t = useDashTheme();
  const [show, setShow] = React.useState(false);
  return (
    <div style={{ position: "relative", width: "100%" }}>
      <input
        {...rest}
        type={show ? "text" : "password"}
        style={{ ...baseField(t, invalid), paddingRight: 40, ...style }}
        onFocus={(e) => { e.target.style.borderColor = t.c.primary; e.target.style.boxShadow = `0 0 0 3px ${t.c.primaryRing}`; rest.onFocus?.(e); }}
        onBlur={(e) => { e.target.style.borderColor = invalid ? t.status.negative : t.c.border; e.target.style.boxShadow = "none"; rest.onBlur?.(e); }}
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        tabIndex={-1}
        title={show ? "Hide password" : "Show password"}
        style={{
          position: "absolute", top: "50%", right: 8, transform: "translateY(-50%)",
          width: 28, height: 28, display: "inline-flex", alignItems: "center", justifyContent: "center",
          border: "none", background: "transparent", color: t.c.textTertiary, cursor: "pointer", borderRadius: 6,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = t.c.text; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = t.c.textTertiary; }}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

export function Textarea({ invalid, rows = 3, style, ...rest }) {
  const t = useDashTheme();
  return (
    <textarea
      rows={rows} {...rest}
      style={{ ...baseField(t, invalid), resize: "vertical", lineHeight: 1.5, ...style }}
      onFocus={(e) => { e.target.style.borderColor = t.c.primary; e.target.style.boxShadow = `0 0 0 3px ${t.c.primaryRing}`; rest.onFocus?.(e); }}
      onBlur={(e) => { e.target.style.borderColor = invalid ? t.status.negative : t.c.border; e.target.style.boxShadow = "none"; rest.onBlur?.(e); }}
    />
  );
}

export function Select({ children, style, ...rest }) {
  const t = useDashTheme();
  return (
    <select {...rest} style={{ ...baseField(t), cursor: "pointer", appearance: "none",
      backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(t.c.textTertiary)}' stroke-width='2'><path d='M6 9l6 6 6-6'/></svg>")`,
      backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center", paddingRight: 32, ...style }}>
      {children}
    </select>
  );
}

// Centered modal dialog with header + body, spring entrance.
export function Modal({ open, onClose, title, subtitle, icon, children, footer, width = 560 }) {
  const t = useDashTheme();
  return createPortal(
    <AnimatePresence>
      {open && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(8,10,18,0.55)", backdropFilter: "blur(4px)" }} />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={t.motion.spring.snappy}
            style={{
              position: "relative", width: "100%", maxWidth: width, maxHeight: "88vh", display: "flex", flexDirection: "column",
              background: t.c.surface, border: `1px solid ${t.c.border}`, borderRadius: t.r.lg, boxShadow: t.shadow.lg,
              overflow: "hidden", fontFamily: t.font.family,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: `${t.s[5]}px ${t.s[5]}px ${t.s[4]}px`, borderBottom: `1px solid ${t.c.border}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                {icon && <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 38, height: 38, borderRadius: 10, background: t.c.primarySoft, color: t.c.primary, flexShrink: 0 }}>{icon}</span>}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: t.font.size.lg, fontWeight: 600, color: t.c.text }}>{title}</div>
                  {subtitle && <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>{subtitle}</div>}
                </div>
              </div>
              <button onClick={onClose} style={{ width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "none", background: t.c.surfaceMuted, color: t.c.textSecondary, cursor: "pointer", flexShrink: 0 }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ padding: t.s[5], overflowY: "auto", flex: 1 }}>{children}</div>
            {footer && <div style={{ display: "flex", justifyContent: "flex-end", gap: t.s[2], padding: `${t.s[4]}px ${t.s[5]}px`, borderTop: `1px solid ${t.c.border}` }}>{footer}</div>}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
